import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import {
  getStageChipColor,
  getStageLabel,
  type WorkflowStageStatus,
} from '../../utils/workflowLanes';
import type { SafeRecoveryModel, SafeRecoveryPrimaryKind } from './diagnosticsTypes';

type DiagnosticsSafeRecoveryPathProps = {
  orgId: string;
  siteId: string;
  deviceId: string | null;
  canReadAudit: boolean;
  canOperate: boolean;
  loading: boolean;
  safeRecovery: SafeRecoveryModel;
  onOpenIncidents: () => void;
  onOpenAudit: () => void;
  onOpenProofPack: () => void;
  onOpenTriage: () => void;
  onOpenOnboard: () => void;
  onSyncDesiredState: () => void;
  onRetryFailedEvidenceOutbox: () => void;
  onRestartUploaders: () => void;
  onRefreshSnapshot: () => void;
  onRollback: () => void;
};

const actionRequiresOperator = (kind: SafeRecoveryPrimaryKind): boolean =>
  kind === 'sync' || kind === 'retry' || kind === 'restart';

export default function DiagnosticsSafeRecoveryPath({
  orgId,
  siteId,
  deviceId,
  canReadAudit,
  canOperate,
  loading,
  safeRecovery,
  onOpenIncidents,
  onOpenAudit,
  onOpenProofPack,
  onOpenTriage,
  onOpenOnboard,
  onSyncDesiredState,
  onRetryFailedEvidenceOutbox,
  onRestartUploaders,
  onRefreshSnapshot,
  onRollback,
}: DiagnosticsSafeRecoveryPathProps) {
  const runPrimaryAction = () => {
    const kind = safeRecovery.primary.kind;
    if (kind === 'nav') return onOpenIncidents();
    if (kind === 'triage') return onOpenTriage();
    if (kind === 'onboard') return onOpenOnboard();
    if (kind === 'sync') return onSyncDesiredState();
    if (kind === 'retry') return onRetryFailedEvidenceOutbox();
    if (kind === 'restart') return onRestartUploaders();
    if (kind === 'refresh') return onRefreshSnapshot();
    return undefined;
  };

  return (
    <Paper sx={{ mb: 3, p: 2 }} variant="outlined">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6">Setup diagnostics - safe recovery path</Typography>
          <Typography variant="body2" color="text.secondary">
            Detect &gt; classify &gt; run the lowest-risk setup action &gt; capture context &gt; escalate or roll back only if
            justified.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={onOpenIncidents}>
            Open Incidents
          </Button>
          <Button size="small" variant="outlined" onClick={onOpenAudit} disabled={!canReadAudit}>
            Audit Log
          </Button>
          {deviceId && (
            <Button size="small" variant="outlined" onClick={onOpenProofPack} disabled={loading}>
              Incident evidence package
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2">Current context</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={`${orgId || '-'} / ${siteId || '-'}`} variant="outlined" />
                <Chip label={`device: ${deviceId || 'all'}`} variant="outlined" />
              </Box>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`heartbeat: ${safeRecovery.hasDevice ? (safeRecovery.heartbeatFresh ? 'fresh' : 'stale') : '-'}`}
                  color={!safeRecovery.hasDevice ? 'default' : safeRecovery.heartbeatFresh ? 'success' : 'warning'}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`drift: ${safeRecovery.driftStatus || 'unknown'}`}
                  color={
                    safeRecovery.driftStatus === 'converged'
                      ? 'success'
                      : safeRecovery.driftStatus === 'drifted'
                        ? 'warning'
                        : 'default'
                  }
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2">Primary next step</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
                {safeRecovery.primary.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {safeRecovery.primary.detail}
              </Typography>
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  color={safeRecovery.primary.kind === 'retry' ? 'warning' : 'primary'}
                  onClick={runPrimaryAction}
                  disabled={actionRequiresOperator(safeRecovery.primary.kind) && !canOperate}
                >
                  {safeRecovery.primary.label}
                </Button>
                {deviceId && canOperate && (
                  <Button size="small" variant="outlined" color="warning" onClick={onRollback}>
                    Rollback (guarded)
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2">Safe-first ladder</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Work top-to-bottom. "Next" is the recommended step.
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {safeRecovery.ladder.map((step) => (
                  <Box key={step.id} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {step.detail}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={getStageLabel(step.status as WorkflowStageStatus)}
                      color={getStageChipColor(step.status as WorkflowStageStatus)}
                      variant="outlined"
                      sx={{ flexShrink: 0 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
}
